import React from 'react';
import { Helmet } from 'react-helmet';
import { useTheme } from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { locales } from '@whitelabel/helpers/site';
import { SITE_NAME } from '@whitelabel/xcover-shared/helpers/constants';
import { PROTOCOL_DOMAIN } from '../../../helpers/url';

interface IPageMetaProps {
  title?: string;
  seoTitle?: string;
  searchDescription?: string;
  socialTitle?: string;
  socialDescription?: string;
  socialImage?: {
    file?: string;
  };
  ogType?: string;
  script?: {
    src: string;
    type?: string;
  }[];
}

const PageMeta = ({
  title,
  seoTitle,
  searchDescription,
  socialTitle,
  socialDescription,
  socialImage,
  ogType,
  script,
}: IPageMetaProps): JSX.Element => {
  const { REACT_APP_MESSAGE_DISPLAY_MODE: MESSAGE_DISPLAY_MODE } = process.env;
  const { locale, bidi } = useIntl();
  const theme = useTheme();
  const location = useLocation();

  return (
    <Helmet defaultTitle={SITE_NAME} titleTemplate={`%s | ${SITE_NAME}`}>
      <html lang={locale} dir={bidi ? 'rtl' : 'ltr'} />

      {(seoTitle || title) && !MESSAGE_DISPLAY_MODE && <title>{seoTitle || title}</title>}

      {searchDescription && <meta name="description" content={searchDescription} />}

      <link rel="mask-icon" href="/static/safari-pinned-tab.svg" color={theme.themeColors.primary} />
      <meta name="theme-color" content={theme.themeColors.primary} />

      <meta property="og:url" content={`${PROTOCOL_DOMAIN}${location.pathname}`} />
      {(socialTitle || seoTitle || title) && <meta property="og:title" content={socialTitle || seoTitle || title} />}
      {(socialDescription || searchDescription) && (
        <meta property="og:description" content={socialDescription || searchDescription} />
      )}
      {socialImage && <meta property="og:image" content={socialImage.file} />}
      {ogType && <meta property="og:type" content={ogType} />}
      <link rel="canonical" href={`${PROTOCOL_DOMAIN}${location.pathname}`} />

      {Object.values(locales).map(({ code }) => (
        <link
          key={code}
          rel="alternate"
          href={`${PROTOCOL_DOMAIN}${location.pathname.replace(`/${locale}`, `/${code}`)}`}
          hrefLang={code}
        />
      ))}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'http://schema.org',
          '@type': 'Organization',
          url: PROTOCOL_DOMAIN,
          name: SITE_NAME,
        })}
      </script>

      {script && script.map((props: any) => <script key={props.src} {...props} />)}
    </Helmet>
  );
};

PageMeta.defaultProps = {
  title: null,
  seoTitle: null,
  searchDescription: null,
  socialTitle: null,
  socialDescription: null,
  socialImage: {},
  ogType: null,
  script: null,
};

export default PageMeta;
